import React, { useEffect, useRef, useState } from 'react';
import IconFont from '$/components/IconFont';
import { useMyIpMediaPreview } from '$/components/MyIpMediaPreview/useMyIpMediaPreview';
import { MyIpMediaPreviewBoundary } from '$/components/MyIpMediaPreview/Boundary';
import NextImage from '$/components/NextImage';
import { getMyIpMediaReferences, getMyIpMediaReferenceKey, getMyIpMediaReferenceUrl } from '$/domains/myIp/mediaReferences';
import type { MyIpMediaReference } from '$/domains/myIp/mediaReferences';
import type { MyIpItem } from '$/domains/myIp/types';
import { getMyIpMediaUrl } from '$/domains/myIp/mediaUrl';
import { resolveVideoCoverUrl } from '$/utils/videoCoverUrl';
import { trackMyIpMaterialClick, trackMyIpMaterialShow } from '$/domains/myIp/tracking';
import { useMyIpCopy } from '$/pages/RoboneoMyIps/useMyIpCopy';
import styles from './index.module.scss';

/** 主音色留在设定页播放器；其余 audio/voice 作为更多素材展示。 */
function isGalleryExtraReference(
  reference: MyIpMediaReference,
  item: MyIpItem,
  urls: Record<string, string>,
  basicUrls: Set<string>,
  selectedVoiceKey?: string,
): boolean {
  if (['three_view', 'color_reference'].includes(reference.role)) return false;
  if (reference.assetId && reference.assetId === item.coverAssetId) return false;
  if (basicUrls.has(getMyIpMediaReferenceUrl(reference, urls) || '')) return false;
  if (reference.role === 'voice') {
    return reference.type === 'audio' && getMyIpMediaReferenceKey(reference) !== selectedVoiceKey;
  }
  return true;
}

/** 卡片展示名称及媒体类型，右栏改用文件格式；两种布局共用授权缩略图和失效处理。 */
function MediaGalleryContent({
  item, urls = {}, compact = false, fillHeight = false,
}: {
  item: MyIpItem;
  urls?: Record<string, string>;
  compact?: boolean;
  /** 固定高度详情面板内填满剩余空间，使空态在筛选栏下方居中。 */
  fillHeight?: boolean;
}) {
  const { media: copy } = useMyIpCopy();
  const [filter, setFilter] = useState<'all' | 'image' | 'video'>('all');
  const [previewFailure, setPreviewFailure] = useState<string>();
  const [failedUrls, setFailedUrls] = useState<string[]>([]);
  const [failedThumbnails, setFailedThumbnails] = useState<string[]>([]);
  const { references, unsupported } = getMyIpMediaReferences(item);
  const basicUrls = new Set([item.coverUrl, ...references.filter((entry) => entry.role === 'three_view').map((entry) => entry.url)]
    .map(getMyIpMediaUrl).filter(Boolean));
  const selectedVoice = references.find((entry) => entry.role === 'voice' && entry.type === 'audio');
  const extra = references.filter((reference) => isGalleryExtraReference(
    reference,
    item,
    urls,
    basicUrls,
    selectedVoice ? getMyIpMediaReferenceKey(selectedVoice) : undefined,
  ));
  const visible = extra.filter((reference) => filter === 'all' || reference.type === filter);
  const media = visible.map((reference) => {
    // Dayu 8131：更多素材 position 从 0 开始，按全部 extra 素材顺序而不是当前筛选结果。
    const position = extra.findIndex((extraItem) => getMyIpMediaReferenceKey(extraItem) === getMyIpMediaReferenceKey(reference));
    const url = getMyIpMediaReferenceUrl(reference, urls) || '';
    return {
      id: getMyIpMediaReferenceKey(reference),
      kind: reference.type,
      url,
      name: reference.name || copy[reference.type],
      watermarkUrl: reference.watermarkUrl,
      thumbnailUrl: reference.thumbnailUrl,
      position,
      canvasSaveLocation: compact ? undefined : 'ip_role_material',
      canvasSaveStatParams: compact ? undefined : {
        ip_id: item.ipId,
        video_duration: reference.type === 'video' && reference.duration !== undefined
          ? Math.round(reference.duration * 1000) : '',
        material_id: reference.assetId || '',
      },
    };
  });
  const exposed = useRef(new Set<string>());
  const preview = useMyIpMediaPreview(media.filter((entry) => !failedUrls.includes(entry.url)), {
    scopeKey: JSON.stringify([item.ipId, item.assetType]),
    onMediaError: (url) => { setPreviewFailure(url); setFailedUrls((previous) => [...previous, url]); },
  });
  const choices = ['all', 'image', 'video'] as const;

  /** 更多素材页签中的有效媒体卡片只曝光一次，筛选切换不会重复上报同一素材。 */
  useEffect(() => {
    if (compact) return;
    media.forEach((entry) => {
      if (!entry.url || entry.kind === 'unknown' || exposed.current.has(entry.id || entry.url)) return;
      exposed.current.add(entry.id || entry.url);
      trackMyIpMaterialShow(item, entry.position, entry.url);
    });
  }, [compact, item, media]);

  return (
    <section className={`${styles.gallery}${compact ? ` ${styles.compactGallery}` : ''}${fillHeight ? ` ${styles.fillHeight}` : ''}`} aria-label={copy.more}>
      {!compact && (
        <div className={styles.mediaFilters} role="group" aria-label={copy.filter}>
          {choices.map((choice) => <button type="button" key={choice} aria-pressed={choice === filter} onClick={() => setFilter(choice)}>{copy[choice]}</button>)}
        </div>
      )}
      {unsupported && <p role="status">{copy.unsupported}</p>}
      {visible.length === 0 && (
        <div className={styles.status} role="status">
          <IconFont name="drawer" fontSize={40} aria-hidden />
          <span>{copy.empty[filter]}</span>
        </div>
      )}
      <ul className={styles.mediaGrid}>
        {visible.map((reference, index) => {
          const url = getMyIpMediaReferenceUrl(reference, urls);
          const available = Boolean(url && !failedUrls.includes(url) && reference.type !== 'unknown');
          const name = reference.name || `${copy[reference.type]} ${index + 1}`;
          // 与创建 IP 页共用封面解析，视频缺少图片封面时使用首帧。
          let thumbnail = getMyIpMediaUrl(reference.thumbnailUrl);
          if (reference.type === 'video') {
            thumbnail = url ? resolveVideoCoverUrl(thumbnail, url) : undefined;
          }
          // 视频/音频的缩略图失败后显示类型占位，不能拿原媒体地址交给 img 再误判整份素材失效。
          let imageUrl = reference.type === 'image' ? url : undefined;
          if (thumbnail && !failedThumbnails.includes(thumbnail)) imageUrl = thumbnail;
          /** 缩略图失败只回落原图，不能把仍可预览/下载的原始资源误标为失效。 */
          const handleImageError = () => {
            const failedThumbnailUrl = imageUrl && imageUrl !== url ? imageUrl : '';
            if (failedThumbnailUrl) setFailedThumbnails((previous) => [...previous, failedThumbnailUrl]);
            else if (url) setFailedUrls((previous) => [...previous, url]);
          };
          const currentMedia = media[index];
          return (
            <li key={getMyIpMediaReferenceKey(reference)}>
              <button
                type="button"
                className={styles.mediaCard}
                disabled={!available}
                onClick={() => {
                  if (currentMedia.url) trackMyIpMaterialClick(item, currentMedia.position, currentMedia.url);
                  preview(currentMedia);
                }}
              >
                {available && imageUrl && (reference.type === 'image' || Boolean(thumbnail)) ? <NextImage src={imageUrl} alt={name} crossOrigin="anonymous" onError={handleImageError} /> : <span className={styles.mediaPlaceholder}>{available ? copy[reference.type] : copy.unavailable}</span>}
                <strong title={name}>{name}</strong>
                <span className={styles.fileType}>{compact ? reference.fileExtension || copy[reference.type] : copy[reference.type]}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {previewFailure && media.some((entry) => entry.url === previewFailure) && <p role="alert">{copy.unavailable}</p>}
    </section>
  );
}

/** 独立导出的素材视图提供按需宿主，详情或资产右栏嵌套使用时不重复挂载预览。 */
export default function MyIpMediaGallery({
  item, urls, compact, fillHeight,
}: Parameters<typeof MediaGalleryContent>[0]) {
  return <MyIpMediaPreviewBoundary><MediaGalleryContent item={item} urls={urls} compact={compact} fillHeight={fillHeight} /></MyIpMediaPreviewBoundary>;
}
